// Starting a quote (round 43).
//
// Only what a quote needs to EXIST: who it is for and what it is called, plus
// the two pipeline facts a person usually knows at the start (the client's
// deadline and who is chasing it). Pricing happens on the quote's own page,
// on the Pricing tab this page lands on.
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { scheduling, type Client } from '../schedulingApi';
import { ApiError } from '../api';
import { notifications } from '../notificationsApi';
import { Banner, Field } from '../ui';
import { PageHeader } from '../components/ui/Page';
import { quotesApi } from '../quotesApi';
import { NewClientModal } from '../components/clients/NewClientModal';

export function NewQuotePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState(params.get('clientId') ?? '');
  const [title, setTitle] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [submissionDueDate, setSubmissionDueDate] = useState('');
  const [quoteManagerId, setQuoteManagerId] = useState('');
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);

  useEffect(() => {
    void scheduling
      .listClients()
      .then((r) => setClients(r.clients))
      .catch(() => setClients([]));
    // The manager-callable people list, never /api/users (admin only): a
    // manager starting a quote must not be refused the dropdown.
    void notifications
      .people()
      .then((r) => setPeople(r.people))
      .catch(() => setPeople([]));
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const created = await quotesApi.create({
        clientId,
        title,
        siteAddress: siteAddress === '' ? null : siteAddress,
        submissionDueDate: submissionDueDate === '' ? null : submissionDueDate,
        // Omitted, not blanked, when the picker could not be shown.
        ...(people.length > 0 ? { quoteManagerId: quoteManagerId === '' ? null : quoteManagerId } : {}),
      });
      navigate(`/app/quotes/${created.quote.id}?tab=pricing`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not create that quote.');
      }
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="New quote"
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => navigate('/app/quotes')}>
              Cancel
            </button>
            <button type="submit" form="quote-form" className="btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create quote'}
            </button>
          </>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}

      <section className="panel max-w-3xl">
        <form id="quote-form" onSubmit={submit} noValidate>
          <div className="mb-4">
            {/* New client in the label row, as on the new-job page: a price
                for somebody new should not mean leaving the form. */}
            <div className="mb-1 flex items-center justify-between gap-2">
              <label className="form-label !mb-0" htmlFor="clientId">
                Client
                {fields.clientId && <span className="field-error-inline">{fields.clientId}</span>}
              </label>
              <button
                type="button"
                className="row-action sm:min-h-0 sm:py-0"
                onClick={() => setNewClientOpen(true)}
              >
                New client
              </button>
            </div>
            <select
              id="clientId"
              className="form-select"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              {/* No "none" option, unlike a job: a quote is always for somebody. */}
              <option value="">Choose a client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          <Field label="Title" name="title" value={title} onChange={setTitle} error={fields.title} />
          <Field
            label="Site address"
            name="siteAddress"
            value={siteAddress}
            onChange={setSiteAddress}
            required={false}
          />
          {/* One field per line: a date input and a select are different
              heights, so side by side their labels sat out of line (seen in
              the guide screenshot, 2026-10-04). */}
          <Field
            label="Submission due"
            name="submissionDueDate"
            type="date"
            value={submissionDueDate}
            onChange={setSubmissionDueDate}
            error={fields.submissionDueDate}
            required={false}
          />
          <p className="hint -mt-1">
            The client&rsquo;s deadline for the price, if they gave one. A draft due within your
            reminder window shows on Home until it is sent.
          </p>
          {people.length > 0 && (
            <label htmlFor="quoteManagerId">
              Quote manager
              <select
                id="quoteManagerId"
                className="form-select"
                value={quoteManagerId}
                onChange={(event) => setQuoteManagerId(event.target.value)}
              >
                <option value="">Nobody yet</option>
                {people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </form>
      </section>

      {newClientOpen && (
        <NewClientModal
          onClose={() => setNewClientOpen(false)}
          onCreated={async (created) => {
            try {
              const r = await scheduling.listClients();
              setClients(r.clients);
            } catch {
              setClients((was) => [...was, created]);
            }
            setClientId(created.id);
            setNewClientOpen(false);
          }}
        />
      )}
    </>
  );
}
